import { AsyncPipe } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	inject,
	OnInit,
	signal,
} from '@angular/core';
import {
	ActivatedRoute,
	Router,
	RouterLink,
	RouterLinkActive,
	RouterOutlet,
} from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import {
	DialogService,
	ENVIRONMENT,
	USER,
	VERSION,
} from '@marmon/frontend/common';
import { ConfigService } from '@marmon/frontend/config';
import { LoggerService } from '@marmon/frontend/logging';
import {
	AccountSettingsDialogComponent,
	AuthService,
	IsAdminPipe,
	LeadershipService,
	NamePipe,
	UserInitialsPipe,
} from '@marmon/frontend/user';
import {
	TuiButton,
	TuiDataList,
	TuiDropdown,
	TuiHint,
	TuiIcon,
} from '@taiga-ui/core';
import { TuiAvatar, TuiBadge, TuiTabs } from '@taiga-ui/kit';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { NavbarComponent } from '../navbar/navbar.component';

type MenuItem = {
	label: string;
	iconStart: string;
	iconEnd?: string;
	handler: () => void;
	order?: number;
};

@Component({
	standalone: true,
	imports: [
		RouterOutlet,
		RouterLink,
		RouterLinkActive,
		TuiAvatar,
		TranslocoPipe,
		UserInitialsPipe,
		NamePipe,
		IsAdminPipe,
		AsyncPipe,
		NavbarComponent,
		TuiDataList,
		TuiDropdown,
		TuiButton,
		TuiIcon,
		TuiBadge,
		TuiHint,
		TuiTabs,
	],
	selector: 'mar-navigation-template',
	templateUrl: 'navigation-template.component.html',
	host: { class: 'h-screen flex flex-col relative' },
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavigationTemplateComponent implements OnInit {
	readonly authService = inject(AuthService);
	readonly leadership = inject(LeadershipService);
	readonly router = inject(Router);
	readonly route = inject(ActivatedRoute);
	readonly user$ = inject(USER);
	readonly environment = inject(ENVIRONMENT);
	readonly config = inject(ConfigService);
	readonly logger = inject(LoggerService);
	readonly version = inject(VERSION);
	readonly dialog = inject(DialogService);

	readonly feedbackItem: MenuItem = {
		label: 'support.action.feedback',
		iconStart: '@tui.message-circle',
		iconEnd: '@tui.external-link',
		handler: () => this.onFeedback(),
		order: 2,
	};

	readonly faqItem: MenuItem = {
		label: 'auth.action.faq',
		iconStart: '@tui.message-circle-question',
		iconEnd: '@tui.external-link',
		handler: () => this.onFAQ(),
		order: 1,
	};

	readonly quickStartItem: MenuItem = {
		label: 'auth.action.quick-start-guide',
		iconStart: '@tui.book-open',
		handler: () => this.onQuickStartGuide(),
		iconEnd: '@tui.external-link',
		order: 0,
	};

	readonly accountSettingsItem: MenuItem = {
		label: 'user.account-settings',
		iconStart: '@tui.settings',
		iconEnd: '@tui.chevron-right',
		handler: () => this.onAccountSettings(),
		order: 50,
	};

	readonly baseItems: MenuItem[] = [
		this.accountSettingsItem,
		{
			label: 'auth.action.sign-out',
			iconStart: '@tui.log-out',
			iconEnd: '@tui.chevron-right',
			handler: () => this.onSignOut(),
			order: 100,
		},
	];

	readonly formUrl = signal<string | null>(null);
	readonly faqUrl = signal<string | null>(null);
	readonly quickStartUrl = signal<string | null>(null);

	readonly menuItems = signal<MenuItem[]>([]);

	ngOnInit() {
		this.setupMenu();
	}

	async setupMenu() {
		const items = [...this.baseItems];

		const [feedback, faq, quickStart] = await Promise.all([
			this.config.getString('feedback_form_url').catch((error) => {
				this.logger.warn('Failed to load feedback form URL', {
					source: 'NavigationTemplate.setupMenu',
					extra: { error },
				});
				return null;
			}),
			this.config.getString('faq_url').catch((error) => {
				this.logger.warn('Failed to load FAQ URL', {
					source: 'NavigationTemplate.setupMenu',
					extra: { error },
				});
				return null;
			}),
			this.config.getString('quick_start_guide_url').catch((error) => {
				this.logger.warn('Failed to load quick start URL', {
					source: 'NavigationTemplate.setupMenu',
					extra: { error },
				});
				return null;
			}),
		]);

		if (feedback) {
			this.formUrl.set(feedback);
			items.unshift(this.feedbackItem);
		}

		if (faq) {
			this.faqUrl.set(faq);
			items.unshift(this.faqItem);
		}

		if (quickStart) {
			this.quickStartUrl.set(quickStart);
			items.unshift(this.quickStartItem);
		}

		this.menuItems.set(
			items.sort((a, b) => {
				const orderA = a.order ?? 0;
				const orderB = b.order ?? 0;
				return orderA - orderB;
			}),
		);
	}

	onSignOut() {
		this.router.navigate(['/sign-out']);
	}

	onAccountSettings() {
		this.dialog
			.open(
				new PolymorpheusComponent(AccountSettingsDialogComponent),
				AccountSettingsDialogComponent.Options(),
			)
			.subscribe();
	}

	onFeedback() {
		const url = this.formUrl();
		if (!url) return;

		window.open(url, '_blank');
	}

	onQuickStartGuide() {
		const url = this.quickStartUrl();
		if (!url) return;

		window.open(url, '_blank');
	}

	onFAQ() {
		const url = this.faqUrl();
		if (!url) return;

		window.open(url, '_blank');
	}
}
